"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
type CardVariant = "default" | "glass" | "solid" | "accent";
type CardContextType = {
  variant: CardVariant;
};
const CardContext = React.createContext<CardContextType>({
  variant: "default"
});
const useCardContext = () => {
  return React.useContext(CardContext);
};
const cardVariants = cva("relative flex flex-col items-stretch overflow-hidden text-card-foreground rounded-[var(--radius-lg,12px)] transition-all duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)]", {
  variants: {
    variant: {
      // Tanpa `border`: --shadow-card sudah memuat ring `0 0 0 1px`, dan memasang
      // keduanya menghasilkan tepi ~2px yang keruh. `accent` tetap pakai border
      // karena ia TIDAK memakai --shadow-card, jadi butuh garisnya sendiri.
      default: "bg-white shadow-[var(--shadow-card)]",
      glass: "glass-card",
      solid: "bg-white shadow-[var(--shadow-card)]",
      accent: "bg-[#F7F7F8] border border-[rgba(0,0,0,0.07)]"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
const cardHeaderVariants = cva("flex items-center justify-between flex-wrap px-5 min-h-14 gap-2.5", {
  variants: {
    variant: {
      default: "border-b border-[rgba(0,0,0,0.07)]",
      glass: "border-b border-[rgba(0,0,0,0.07)]",
      solid: "border-b border-[rgba(0,0,0,0.07)]",
      accent: ""
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
const cardContentVariants = cva("grow p-5", {
  variants: {
    variant: {
      default: "",
      glass: "",
      solid: "",
      accent: "bg-white rounded-t-[10px] [&:last-child]:rounded-b-[10px]"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
const cardFooterVariants = cva("flex items-center px-5 min-h-14", {
  variants: {
    variant: {
      default: "border-t border-[rgba(0,0,0,0.07)]",
      glass: "border-t border-[rgba(0,0,0,0.07)]",
      solid: "border-t border-[rgba(0,0,0,0.07)]",
      accent: "bg-white rounded-b-[10px] mt-[2px]"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
function Card(t0) {
  const $ = _c(15);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const variant = t1 === undefined ? "default" : t1;
  const t2 = (variant || "default") as CardVariant;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      variant: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== className || $[7] !== variant) {
    t4 = cn(cardVariants({
      variant
    }), className);
    $[6] = className;
    $[7] = variant;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== props || $[10] !== t4) {
    t5 = <div data-slot="card" className={t4} {...props} />;
    $[9] = props;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <CardContext.Provider value={t3}>{t5}</CardContext.Provider>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function CardHeader(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    variant
  } = useCardContext();
  let t1;
  if ($[3] !== className || $[4] !== variant) {
    t1 = cn(cardHeaderVariants({
      variant
    }), className);
    $[3] = className;
    $[4] = variant;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <div data-slot="card-header" className={t1} {...props} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function CardContent(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    variant
  } = useCardContext();
  let t1;
  if ($[3] !== className || $[4] !== variant) {
    t1 = cn(cardContentVariants({
      variant
    }), className);
    $[3] = className;
    $[4] = variant;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <div data-slot="card-content" className={t1} {...props} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function CardFooter(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    variant
  } = useCardContext();
  let t1;
  if ($[3] !== className || $[4] !== variant) {
    t1 = cn(cardFooterVariants({
      variant
    }), className);
    $[3] = className;
    $[4] = variant;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <div data-slot="card-footer" className={t1} {...props} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function CardHeading(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("space-y-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="card-heading" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function CardToolbar(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center gap-2.5", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="card-toolbar" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function CardTitle(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-base font-semibold leading-none tracking-tight", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <h2 data-slot="card-title" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function CardDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="card-description" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function CardTable(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("grid grow", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="card-table" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardHeading, CardTable, CardTitle, CardToolbar };